import type React from "react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { isAgencyFinanceUser } from "@/api/user";
import { SHOW_LANDING_PAYMENT_TERMS_LINK } from "@/shared/legal/administrationFee";
import { canAccessAdmin } from "@/features/admin";
import { CreatorOnboardingRoute } from "@/features/creator-onboarding";
import { NotificationBell } from "@/features/notifications";
import { ForgotPassword, Login, Logout, ResetPassword, RoleRegister } from "@/pages/auth";
import GoogleCallback from "@/pages/auth/GoogleCallback";
import OrganizationLogin from "@/pages/auth/OrganizationLogin";
import UpdateUser from "@/pages/auth/UpdateUser";
import { FullPageLoader } from "@/shared/components/Loading/Loading";
import { useAuth } from "@/shared/contexts/AuthContext";
import ProtectedRoute from "@/shared/guards/ProtectedRoute";
import PublicRoute from "@/shared/guards/PublicRoute";
import { RootLayout } from "@/shared/layout/RootLayout";

const AddCreator = lazy(() => import("@/pages/AddCreator").then((m) => ({ default: m.AddCreator })));
const CampaignDetail = lazy(() => import("@/pages/CampaignDetail").then((m) => ({ default: m.CampaignDetail })));
const CampaignList = lazy(() => import("@/pages/CampaignList").then((m) => ({ default: m.CampaignList })));
const CampaignReport = lazy(() => import("@/pages/CampaignReport").then((m) => ({ default: m.default })));
const CreateGroup = lazy(() => import("@/pages/CreateGroup").then((m) => ({ default: m.CreateGroup })));
const CustomizeColumns = lazy(() => import("@/pages/CustomizeColumns").then((m) => ({ default: m.CustomizeColumns })));
const DataDashboard = lazy(() => import("@/pages/DataDashboard").then((m) => ({ default: m.default })));
const GenerativeAIAnalysis = lazy(() =>
	import("@/pages/GenerativeAIAnalysis").then((m) => ({ default: m.GenerativeAIAnalysis })),
);
const InviteCreator = lazy(() => import("@/pages/InviteCreator").then((m) => ({ default: m.InviteCreator })));
const CreatorNetwork = lazy(() => import("@/pages/CreatorNetwork").then((m) => ({ default: m.default })));
const PublicShortUrlRedirect = lazy(() =>
	import("@/pages/PublicShortUrlRedirect").then((m) => ({ default: m.PublicShortUrlRedirect })),
);
const Resource = lazy(() => import("@/pages/Resource").then((m) => ({ default: m.default })));
const ShortUrlRedirect = lazy(() => import("@/pages/ShortUrlRedirect").then((m) => ({ default: m.ShortUrlRedirect })));
const SimilarCreators = lazy(() => import("@/pages/SimilarCreator").then((m) => ({ default: m.default })));
const TiktokCallBack = lazy(() => import("@/pages/TiktokCallBack").then((m) => ({ default: m.TiktokCallBack })));
const TiktokBusinessLinkCallback = lazy(() =>
	import("@/pages/TiktokBusinessLinkCallback").then((m) => ({ default: m.TiktokBusinessLinkCallback })),
);
const TiktokGet = lazy(() => import("@/pages/TiktokGet").then((m) => ({ default: m.TiktokGet })));
const TTCMDiscoverCreator = lazy(() =>
	import("@/pages/TTCMDiscoverCreator").then((m) => ({ default: m.TTCMDiscoverCreator })),
);
const TTODiscoverCreator = lazy(() =>
	import("@/pages/TTODiscoverCreator").then((m) => ({ default: m.TTODiscoverCreator })),
);
const CreatorStatements = lazy(() =>
	import("@/pages/CreatorStatements").then((m) => ({ default: m.CreatorStatements })),
);
const CreatorAcceptTerms = lazy(() =>
	import("@/pages/CreatorAcceptTerms").then((m) => ({ default: m.CreatorAcceptTerms })),
);
const TermsOfService = lazy(() => import("@/pages/TermsOfService").then((m) => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() =>
	import("@/shared/legal/PrivacyPolicyDocument").then((m) => ({ default: m.PrivacyPolicyDocument })),
);
const FeeSchedule = lazy(() =>
	import("@/shared/legal/PaymentTermsDocument").then((m) => ({ default: m.PaymentTermsDocument })),
);
const CreatorInvitation = lazy(() => import("@/pages/CreatorInvitation"));
const CreatorInvitationRequestNewLink = lazy(() => import("@/pages/CreatorInvitationRequestNewLink"));
const MyAccount = lazy(() => import("@/pages/MyAccount"));
const LandingPage = lazy(() => import("@/pages/LandingPage").then((m) => ({ default: m.LandingPage })));
const FinanceCampaignList = lazy(() =>
	import("@/pages/FinanceCampaignList").then((module) => ({ default: module.FinanceCampaignList })),
);
const FinanceCampaignInvoices = lazy(() =>
	import("@/pages/FinanceCampaignInvoices").then((module) => ({ default: module.FinanceCampaignInvoices })),
);
const Admin = lazy(() => import("@/pages/Admin").then((module) => ({ default: module.Admin })));
const NotFound = lazy(() => import("@/pages/NotFound").then((m) => ({ default: m.NotFound })));

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
	<Suspense fallback={<FullPageLoader />}>{children}</Suspense>
);

const CampaignListRoute = () => {
	const { user } = useAuth();
	return isAgencyFinanceUser(user) ? <FinanceCampaignList /> : <CampaignList />;
};

const CampaignDetailRoute = () => {
	const { user } = useAuth();
	return isAgencyFinanceUser(user) ? <FinanceCampaignInvoices /> : <CampaignDetail />;
};

// Ticket-348: admins and Finance-subrole agency users; each tab applies its own gate (the backend does too).
const AdminRoute = () => {
	const { user } = useAuth();
	return canAccessAdmin(user) ? <Admin /> : <Navigate to="/campaigns" replace />;
};

const authPageVariants = {
	initial: { opacity: 0 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: -10 },
};

const AuthPageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	return (
		<motion.div
			variants={authPageVariants}
			initial="initial"
			animate="animate"
			exit="exit"
			transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
			style={{ width: "100%" }}
		>
			{children}
		</motion.div>
	);
};

export const AppRoutes = () => {
	const location = useLocation();

	return (
		<AnimatePresence mode="wait" initial={false}>
			<Routes location={location} key={location.pathname}>
				<Route element={<PublicRoute />}>
					<Route
						path="/"
						element={
							<LazyRoute>
								<LandingPage />
							</LazyRoute>
						}
					/>
					<Route
						path="/login"
						element={
							<AuthPageTransition>
								<Login />
							</AuthPageTransition>
						}
					/>
					<Route
						path="/register"
						element={
							<AuthPageTransition>
								<RoleRegister />
							</AuthPageTransition>
						}
					/>
					<Route
						path="/forgot-password"
						element={
							<AuthPageTransition>
								<ForgotPassword />
							</AuthPageTransition>
						}
					/>
					<Route
						path="/amplify/login"
						element={
							<AuthPageTransition>
								<OrganizationLogin />
							</AuthPageTransition>
						}
					/>
				</Route>
				<Route
					path="/terms-of-service"
					element={
						<LazyRoute>
							<TermsOfService />
						</LazyRoute>
					}
				/>
				<Route
					path="/privacy-policy"
					element={
						<LazyRoute>
							<PrivacyPolicy />
						</LazyRoute>
					}
				/>
				{/* Old links and the auth footer used /privacy */}
				<Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
				{SHOW_LANDING_PAYMENT_TERMS_LINK && (
					<Route
						path="/fee-schedule"
						element={
							<LazyRoute>
								<FeeSchedule />
							</LazyRoute>
						}
					/>
				)}

				<Route
					path="/reset-password"
					element={
						<AuthPageTransition>
							<ResetPassword />
						</AuthPageTransition>
					}
				/>
				<Route
					path="/amplify/reset-password"
					element={
						<AuthPageTransition>
							<ResetPassword />
						</AuthPageTransition>
					}
				/>
				<Route
					path="/amplify/update-user"
					element={
						<AuthPageTransition>
							<UpdateUser />
						</AuthPageTransition>
					}
				/>
				<Route path="/auth/callback" element={<GoogleCallback />} />
				<Route
					path="/tiktok-business-link-auth"
					element={
						<LazyRoute>
							<TiktokBusinessLinkCallback />
						</LazyRoute>
					}
				/>
				<Route path="/logout" element={<Logout />} />
				<Route
					path="/creator-invitations/request-new-link/:token"
					element={
						<AuthPageTransition>
							<LazyRoute>
								<CreatorInvitationRequestNewLink />
							</LazyRoute>
						</AuthPageTransition>
					}
				/>
				<Route
					path="/creator-invitations/:token"
					element={
						<AuthPageTransition>
							<CreatorInvitation />
						</AuthPageTransition>
					}
				/>

				<Route
					path="/p/:code"
					element={
						<LazyRoute>
							<PublicShortUrlRedirect />
						</LazyRoute>
					}
				/>

				<Route element={<ProtectedRoute />}>
					<Route element={<CreatorOnboardingRoute />}>
						<Route
							path="/r/:code"
							element={
								<LazyRoute>
									<ShortUrlRedirect />
								</LazyRoute>
							}
						/>
						<Route
							path="/tiktok-auth"
							element={
								<LazyRoute>
									<TiktokCallBack />
								</LazyRoute>
							}
						/>
						<Route element={<RootLayout sidebarExtra={<NotificationBell />} />}>
							<Route
								path="/admin"
								element={
									<LazyRoute>
										<AdminRoute />
									</LazyRoute>
								}
							/>
							<Route
								path="/admin/:tab"
								element={
									<LazyRoute>
										<AdminRoute />
									</LazyRoute>
								}
							/>
							<Route
								path="/account"
								element={
									<LazyRoute>
										<MyAccount />
									</LazyRoute>
								}
							/>
							<Route
								path="/campaigns"
								element={
									<LazyRoute>
										<CampaignListRoute />
									</LazyRoute>
								}
							/>
							<Route
								path="/campaign-detail"
								element={
									<LazyRoute>
										<CampaignDetailRoute />
									</LazyRoute>
								}
							/>
							<Route
								path="/campaign-report"
								element={
									<LazyRoute>
										<CampaignReport />
									</LazyRoute>
								}
							/>
							<Route
								path="/campaign-detail/add-creator"
								element={
									<LazyRoute>
										<AddCreator />
									</LazyRoute>
								}
							/>
							<Route
								path="/campaign-detail/customize-columns"
								element={
									<LazyRoute>
										<CustomizeColumns />
									</LazyRoute>
								}
							/>
							<Route
								path="/campaign-detail/create-new-group"
								element={
									<LazyRoute>
										<CreateGroup />
									</LazyRoute>
								}
							/>
							<Route
								path="/tiktok-get"
								element={
									<LazyRoute>
										<TiktokGet />
									</LazyRoute>
								}
							/>
							<Route
								path="/tiktok/similar-creator"
								element={
									<LazyRoute>
										<SimilarCreators />
									</LazyRoute>
								}
							/>
							<Route
								path="/tiktok/ttcm/discover-creator"
								element={
									<LazyRoute>
										<TTCMDiscoverCreator />
									</LazyRoute>
								}
							/>
							<Route
								path="/tiktok/tto/discover-creator"
								element={
									<LazyRoute>
										<TTODiscoverCreator />
									</LazyRoute>
								}
							/>
							<Route
								path="/creator-network"
								element={
									<LazyRoute>
										<CreatorNetwork />
									</LazyRoute>
								}
							/>
							<Route
								path="/invite-creator"
								element={
									<LazyRoute>
										<InviteCreator />
									</LazyRoute>
								}
							/>
							<Route
								path="/data-dashboard"
								element={
									<LazyRoute>
										<DataDashboard />
									</LazyRoute>
								}
							/>
							<Route
								path="/resource"
								element={
									<LazyRoute>
										<Resource />
									</LazyRoute>
								}
							/>
							<Route
								path="/generative-ai-analysis"
								element={
									<LazyRoute>
										<GenerativeAIAnalysis />
									</LazyRoute>
								}
							/>
							<Route
								path="/creator/payment-profile"
								// Deprecated page: bank and tax details now live on the account page.
								element={<Navigate to="/account?tab=bank" replace />}
							/>
							<Route
								path="/creator/statements"
								element={
									<LazyRoute>
										<CreatorStatements />
									</LazyRoute>
								}
							/>
							<Route
								path="/creator/statements/:statementId"
								element={
									<LazyRoute>
										<CreatorStatements />
									</LazyRoute>
								}
							/>
							<Route
								path="/creator/engagements/:engagementId/accept-terms"
								element={
									<LazyRoute>
										<CreatorAcceptTerms />
									</LazyRoute>
								}
							/>
						</Route>
					</Route>
				</Route>

				<Route
					path="*"
					element={
						<LazyRoute>
							<NotFound />
						</LazyRoute>
					}
				/>
			</Routes>
		</AnimatePresence>
	);
};
